import { Box, Card, CardContent, Checkbox, Collapse, FormControlLabel, FormGroup, Switch, Typography, useTheme } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FormHandles } from '@unform/core';
import { Form } from '../../../shared/forms';
import { VTextField } from '../../../shared/forms';
import dayjs, { Dayjs } from 'dayjs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IPropsBusinessPage } from '../Business';
import { businessService, feedback, IUpdateBusiness } from '../../../shared/services';
import { Button, TimePicker } from '../../../shared/components';
import { PAYMENT_METHODS } from '../../../shared/constants/paymentMethods';

export const BusinessFinancialSettings: React.FC<IPropsBusinessPage> = ({ business, refetch }) => {
  const theme = useTheme();
  const formRef = useRef<FormHandles>(null);
  const queryClient = useQueryClient();

  // ── Local state for all editable fields ──────────────────────

  const [requireOpenCaixa, setRequireOpenCaixa] = useState(business.requireOpenCaixa ?? false);
  const [autoCloseEnabled, setAutoCloseEnabled] = useState(business.autoCloseCaixaEnabled ?? false);
  const [autoCloseTime, setAutoCloseTime] = useState<Dayjs | null>(
    business.autoCloseCaixaTime ? dayjs(business.autoCloseCaixaTime, 'HH:mm:ss') : null
  );
  const [paymentMethods, setPaymentMethods] = useState<string[]>(
    () => business.paymentMethods?.split(',').map(s => s.trim()).filter(Boolean) || []
  );

  // Snapshot for dirty detection
  const initialSnapshotRef = useRef({
    requireOpenCaixa: business.requireOpenCaixa ?? false,
    autoCloseEnabled: business.autoCloseCaixaEnabled ?? false,
    autoCloseTime: business.autoCloseCaixaTime ? dayjs(business.autoCloseCaixaTime, 'HH:mm:ss') : null,
    paymentMethods: business.paymentMethods?.split(',').map(s => s.trim()).filter(Boolean) || [],
  });

  // Sync local state when business prop changes (e.g. after save)
  useEffect(() => {
    setRequireOpenCaixa(business.requireOpenCaixa ?? false);
    setAutoCloseEnabled(business.autoCloseCaixaEnabled ?? false);
    setAutoCloseTime(business.autoCloseCaixaTime ? dayjs(business.autoCloseCaixaTime, 'HH:mm:ss') : null);
    setPaymentMethods(business.paymentMethods?.split(',').map(s => s.trim()).filter(Boolean) || []);
    initialSnapshotRef.current = {
      requireOpenCaixa: business.requireOpenCaixa ?? false,
      autoCloseEnabled: business.autoCloseCaixaEnabled ?? false,
      autoCloseTime: business.autoCloseCaixaTime ? dayjs(business.autoCloseCaixaTime, 'HH:mm:ss') : null,
      paymentMethods: business.paymentMethods?.split(',').map(s => s.trim()).filter(Boolean) || [],
    };
  }, [business]);

  // ── Dirty detection ──────────────────────────────────────────

  const isDirty = useMemo(() => {
    const init = initialSnapshotRef.current;
    return (
      requireOpenCaixa !== init.requireOpenCaixa ||
      autoCloseEnabled !== init.autoCloseEnabled ||
      (autoCloseTime?.format('HH:mm') ?? '') !== (init.autoCloseTime?.format('HH:mm') ?? '') ||
      JSON.stringify([...paymentMethods].sort()) !== JSON.stringify([...init.paymentMethods].sort())
    );
  }, [requireOpenCaixa, autoCloseEnabled, autoCloseTime, paymentMethods]);

  // ── Single save mutation ─────────────────────────────────────

  const { mutate: saveSettings, isLoading: isLoadingSave } = useMutation(
    () => {
      const formData = formRef.current?.getData() || {};
      const cardFeeDebit = parseFloat(formData.cardFeeDebit as string) || business.cardFeeDebit;
      const cardFeeCredit = parseFloat(formData.cardFeeCredit as string) || business.cardFeeCredit;

      const payload: IUpdateBusiness = {
        id: business.id,
        name: business.name,
        address: business.address,
        cnpj: business.cnpj,
        profilePicture: business.profilePicture,
        hideFromSearch: business.hideFromSearch,
        cardFeeDebit,
        cardFeeCredit,
        appointmentLeadTimeMinutes: business.appointmentLeadTimeMinutes,
        requireOpenCaixa,
        autoCloseCaixaEnabled: autoCloseEnabled,
        autoCloseCaixaTime: autoCloseEnabled && autoCloseTime ? autoCloseTime!.format('HH:mm:ss') : undefined,
        paymentMethods: paymentMethods.length > 0 ? paymentMethods.join(', ') : undefined,
      };

      return businessService.update(payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['business']);
        refetch();
        feedback('Configurações financeiras salvas', 'success');
      },
      onError: (error: Error) => feedback(error.message, 'error'),
    }
  );

  // ── Handlers (local state only, no auto-save) ────────────────

  const handlePaymentMethodToggle = (method: string) => {
    setPaymentMethods(prev =>
      prev.includes(method)
        ? prev.filter(m => m !== method)
        : [...prev, method]
    );
  };

  const handleSave = useCallback(() => {
    saveSettings();
  }, [saveSettings]);

  return (
    <Box display='flex' flexDirection='column' gap={4}>
      {/* Card Fee Configuration */}
      <Card sx={{ bgcolor: theme.palette.background.paper }}>
        <CardContent>
          <Box display='flex' flexDirection='column' gap={2}>
            <Typography variant='h6'>Taxas de Cartão</Typography>
            <Typography variant='body2' color='text.secondary'>
              Defina as taxas cobradas nas vendas com cartão de crédito e débito.
            </Typography>
            <Form ref={formRef} initialData={business} onSubmit={() => {/* handled by Salvar button */}}>
              <Box display='flex' gap={2}>
                <VTextField name='cardFeeDebit' label='Taxa Débito (%)' type='number' inputProps={{ step: 0.1 }} sx={{ width: 150 }} />
                <VTextField name='cardFeeCredit' label='Taxa Crédito (%)' type='number' inputProps={{ step: 0.1 }} sx={{ width: 150 }} />
              </Box>
            </Form>
          </Box>
        </CardContent>
      </Card>

      {/* Caixa Requirement */}
      <Card sx={{ bgcolor: theme.palette.background.paper }}>
        <CardContent>
          <Box display='flex' flexDirection='column' gap={2}>
            <Typography variant='h6'>Exigência de Caixa Aberto</Typography>
            <Typography variant='body2' color='text.secondary'>
              Quando ativado, bloqueia operações financeiras (pagamentos, comandas, transações)
              se não houver um caixa aberto para o estabelecimento.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={requireOpenCaixa}
                  onChange={(e) => setRequireOpenCaixa(e.target.checked)}
                />
              }
              label="Exigir caixa aberto para operações financeiras"
            />
          </Box>
        </CardContent>
      </Card>

      {/* Auto Close Caixa */}
      <Card sx={{ bgcolor: theme.palette.background.paper }}>
        <CardContent>
          <Box display='flex' flexDirection='column' gap={2}>
            <Typography variant='h6'>Fechamento Automático do Caixa</Typography>
            <Typography variant='body2' color='text.secondary'>
              Quando ativado, o caixa será fechado automaticamente todos os dias no horário definido.
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={autoCloseEnabled}
                  onChange={(e) => setAutoCloseEnabled(e.target.checked)}
                />
              }
              label="Fechar caixa automaticamente"
            />
            <Collapse in={autoCloseEnabled}>
              <Box mt={1}>
                <TimePicker
                  label='Horário de fechamento'
                  value={autoCloseTime}
                  onChange={(value) => setAutoCloseTime(value)}
                />
              </Box>
            </Collapse>
          </Box>
        </CardContent>
      </Card>

      {/* Payment Methods */}
      <Card sx={{ bgcolor: theme.palette.background.paper }}>
        <CardContent>
          <Box display='flex' flexDirection='column' gap={2}>
            <Typography variant='h6'>Formas de Pagamento</Typography>
            <Typography variant='body2' color='text.secondary'>
              Selecione as formas de pagamento aceitas pelo estabelecimento.
            </Typography>
            <FormGroup row>
              {PAYMENT_METHODS.map((method) => (
                <FormControlLabel
                  key={method}
                  control={
                    <Checkbox
                      checked={paymentMethods.includes(method)}
                      onChange={() => handlePaymentMethodToggle(method)}
                    />
                  }
                  label={method}
                />
              ))}
            </FormGroup>
          </Box>
        </CardContent>
      </Card>

      {/* Global Save Button */}
      <Box display='flex' justifyContent='flex-end'>
        <Button
          label='Salvar'
          variant='contained'
          onClick={handleSave}
          isLoading={isLoadingSave}
          disabled={!isDirty}
        />
      </Box>
    </Box>
  );
};
